import type * as RoutingTypes from './types';
import { createEndpoint } from './helpers';

// Helper function to define endpoints
const defineEndpoints = <T extends Record<string, ReturnType<typeof createEndpoint>>>(endpoints: T) => endpoints;

// Config endpoints
const configEndpoints = defineEndpoints({
  'config/get': createEndpoint<RoutingTypes.API_ConfigResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/config/get',
    method: 'GET',
  }),
  'schema/get': createEndpoint<RoutingTypes.API_SchemaField[], RoutingTypes.API_GenericErrorResponse>({
    url: '/schema/:name',
    method: 'GET',
  }),
});

const registrationEndpoints = defineEndpoints({
  'registration/user': createEndpoint<
    RoutingTypes.API_RegistrationUserResponse,
    RoutingTypes.API_GenericErrorResponse,
    RoutingTypes.API_RegistrationUserRequest
  >({
    url: '/registration/user',
    method: 'POST',
  }),
});

const authEndpoints = defineEndpoints({
  'auth/login': createEndpoint<RoutingTypes.API_AuthLoginResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_AuthLoginRequest>({
    url: '/auth/login',
    method: 'POST',
  }),
  'auth/logout': createEndpoint<RoutingTypes.API_AuthGenericResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/auth/logout',
    method: 'GET',
  }),
  'auth/verify': createEndpoint<RoutingTypes.API_AuthGenericResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/auth/verify',
    method: 'GET',
  }),
  'auth/profile': createEndpoint<RoutingTypes.API_AuthProfileResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/auth/profile',
    method: 'GET',
  }),
});

const inboxEndpoints = defineEndpoints({
  'mail/list': createEndpoint<RoutingTypes.API_MailListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/mail/list',
    method: 'GET',
  }),
  'mail/get': createEndpoint<RoutingTypes.API_MailGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/mail/get/:id',
    method: 'GET',
  }),
});

const locationsEndpoints = defineEndpoints({
  'locations/mail': createEndpoint<RoutingTypes.API_LocationsResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/locations/mail',
    method: 'GET',
  }),
  'locations/opportunities': createEndpoint<RoutingTypes.API_PointFeatureCollection, RoutingTypes.API_GenericErrorResponse>({
    url: '/locations/opportunities',
    method: 'GET',
  }),

  'locations/opportunities/point-routes': createEndpoint<RoutingTypes.API_LocationsResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/locations/opportunities/point-routes',
    method: 'GET',
  }),
  'locations/opportunities/point-route': createEndpoint<RoutingTypes.API_LocationsResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/locations/opportunities/point-route',
    method: 'GET',
  }),
  'locations/vehicles': createEndpoint<RoutingTypes.API_FeatureCollection, RoutingTypes.API_GenericErrorResponse>({
    url: '/locations/vehicles',
    method: 'GET',
  }),
});

const vehicleEndpoints = defineEndpoints({
  'vehicles/list': createEndpoint<RoutingTypes.API_VehicleListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/vehicles/list',
    method: 'GET',
  }),
  'vehicles/get': createEndpoint<RoutingTypes.API_VehicleGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/vehicles/get/:id',
    method: 'GET',
  }),
  'vehicles/create': createEndpoint<RoutingTypes.API_VehicleListResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Vehicle>({
    url: '/vehicles/create',
    method: 'POST',
  }),
  'vehicles/update': createEndpoint<RoutingTypes.API_VehicleListResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Vehicle>({
    url: '/vehicles/update',
    method: 'PUT',
  }),
  'vehicles/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/vehicles/delete',
    method: 'DELETE',
  }),
  'vehicles/depots': createEndpoint<RoutingTypes.API_Depot[], RoutingTypes.API_GenericErrorResponse>({
    url: '/vehicles/depots',
    method: 'GET',
  }),
});

const depotEndpoints = defineEndpoints({
  'depots/list': createEndpoint<RoutingTypes.API_DepotListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/depots/list',
    method: 'GET',
  }),
  // 'depots/get': createEndpoint<RoutingTypes.API_DepotGetResponse, RoutingTypes.API_GenericErrorResponse>({
  //   url: '/depots/get/:id',
  //   method: 'GET',
  // }),
  'depots/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Depot>({
    url: '/depots/create',
    method: 'POST',
  }),
  'depots/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Depot>({
    url: '/depots/update',
    method: 'PUT',
  }),
  'depots/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/depots/delete',
    method: 'DELETE',
  }),
});
const capabilitiesEndpoints = defineEndpoints({
  'capabilities/list': createEndpoint<RoutingTypes.API_CapabilityListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/capabilities/list',
    method: 'GET',
  }),
  'capabilities/get': createEndpoint<RoutingTypes.API_CapabilityGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/capabilities/get/:id',
    method: 'GET',
  }),
  'capabilities/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Capability>({
    url: '/capabilities/create',
    method: 'POST',
  }),
  'capabilities/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Capability>({
    url: '/capabilities/update',
    method: 'PUT',
  }),
  'capabilities/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/capabilities/delete',
    method: 'DELETE',
  }),
});

const lineassignmentEndpoints = defineEndpoints({
  'lineassignments/list': createEndpoint<RoutingTypes.API_LineAssignmentListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/lineassignments/list',
    method: 'GET',
  }),
  'lineassignments/get': createEndpoint<RoutingTypes.API_LineAssignmentGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/lineassignments/get/:id',
    method: 'GET',
  }),
  'lineassignments/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_LineAssignment>({
    url: '/lineassignments/create',
    method: 'POST',
  }),
  'lineassignments/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_LineAssignment>({
    url: '/lineassignments/update',
    method: 'PUT',
  }),
  'lineassignments/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/lineassignments/delete',
    method: 'DELETE',
  }),
});

const driverEndpoints = defineEndpoints({
  'drivers/list': createEndpoint<RoutingTypes.API_DriverListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/drivers/list',
    method: 'GET',
  }),
  'drivers/get': createEndpoint<RoutingTypes.API_DriverGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/drivers/get/:id',
    method: 'GET',
  }),
  'drivers/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Driver>({
    url: '/drivers/create',
    method: 'POST',
  }),
  'drivers/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Driver>({
    url: '/drivers/update',
    method: 'PUT',
  }),
  'drivers/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/drivers/delete/:id',
    method: 'DELETE',
  }),
});

const scheduleEndpoints = defineEndpoints({
  'schedules/list': createEndpoint<RoutingTypes.API_ScheduleListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/schedules/list',
    method: 'GET',
  }),
  'schedules/get': createEndpoint<RoutingTypes.API_ScheduleGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/schedules/:id',
    method: 'GET',
  }),
  'schedules/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Schedule>({
    url: '/schedules/create',
    method: 'POST',
  }),
  'schedules/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Schedule>({
    url: '/schedules/update',
    method: 'PUT',
  }),
  'schedules/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/schedules/delete',
    method: 'DELETE',
  }),
});

const addressEndpoints = defineEndpoints({
  'addresses/list': createEndpoint<RoutingTypes.API_AddressListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/addresses/list',
    method: 'GET',
  }),
  'addresses/get': createEndpoint<RoutingTypes.API_AddressGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/addresses/get/:id',
    method: 'GET',
  }),
  'addresses/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Address>({
    url: '/addresses/create',
    method: 'POST',
  }),
  'addresses/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Address>({
    url: '/addresses/update',
    method: 'PUT',
  }),
  'addresses/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/addresses/delete',
    method: 'DELETE',
  }),
});
const operationsEndpoints = defineEndpoints({
  'operations/list': createEndpoint<RoutingTypes.API_OperationsListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/operations/list',
    method: 'GET',
  }),
  'operations/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Operation>({
    url: '/operations/create',
    method: 'POST',
  }),
  'operations/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Operation>({
    url: '/operations/update',
    method: 'PUT',
  }),
  'operations/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/operations/delete',
    method: 'DELETE',
  }),
});
const contactEndpoints = defineEndpoints({
  'contacts/list': createEndpoint<RoutingTypes.API_ContactListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/contacts/list',
    method: 'GET',
  }),
  'contacts/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Contact>({
    url: '/contacts/create',
    method: 'POST',
  }),
  'contacts/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Contact>({
    url: '/contacts/update',
    method: 'PUT',
  }),
  'contacts/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/contacts/delete',
    method: 'DELETE',
  }),
});

const customerEndpoints = defineEndpoints({
  'customers/list': createEndpoint<RoutingTypes.API_CustomerListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/customers/list',
    method: 'GET',
  }),
  'customers/get': createEndpoint<RoutingTypes.API_CustomerGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/customers/get/:id',
    method: 'GET',
  }),
  'customers/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Customer>({
    url: '/customers/create',
    method: 'POST',
  }),
  'customers/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Customer>({
    url: '/customers/update',
    method: 'PUT',
  }),
  'customers/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/customers/delete/:id',
    method: 'DELETE',
  }),
});

const supplierEndpoints = defineEndpoints({
  'suppliers/list': createEndpoint<RoutingTypes.API_SupplierListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/suppliers/list',
    method: 'GET',
  }),
  'suppliers/get': createEndpoint<RoutingTypes.API_SupplierGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/suppliers/get/:id',
    method: 'GET',
  }),
  'suppliers/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Customer>({
    url: '/suppliers/create',
    method: 'POST',
  }),
  'suppliers/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Customer>({
    url: '/suppliers/update',
    method: 'PUT',
  }),
  'suppliers/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/suppliers/delete/:id',
    method: 'DELETE',
  }),
});

const articleEndpoints = defineEndpoints({
  'articles/list': createEndpoint<RoutingTypes.API_ArticleListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/articles/list',
    method: 'GET',
  }),
  'articles/get': createEndpoint<RoutingTypes.API_ArticleGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/articles/get/:id',
    method: 'GET',
  }),
  'articles/sku': createEndpoint<RoutingTypes.API_ArticleGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/articles/sku/:sku',
    method: 'GET',
  }),
  'articles/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_ArticleCreateRequest>({
    url: '/articles/create',
    method: 'POST',
  }),
  'articles/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_ArticleUpdateRequest>({
    url: '/articles/update/:id',
    method: 'PUT',
  }),
  'articles/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/articles/delete/:id',
    method: 'DELETE',
  }),
});

const orderEndpoints = defineEndpoints({
  'orders/list': createEndpoint<RoutingTypes.API_OrderListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/orders/list',
    method: 'GET',
  }),
  'orders/get': createEndpoint<RoutingTypes.API_OrderGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/orders/get/:id',
    method: 'GET',
  }),
  'orders/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Order>({
    url: '/orders/create',
    method: 'POST',
  }),
  'orders/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Order>({
    url: '/orders/update',
    method: 'PUT',
  }),
  'orders/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/orders/delete',
    method: 'DELETE',
  }),
  'orders/create-route-calculation': createEndpoint<
    RoutingTypes.API_OrderCreateRouteResponse,
    RoutingTypes.API_GenericErrorResponse,
    RoutingTypes.API_OrderCreateRouteRequest
  >({
    url: '/orders/create-route-calculation',
    method: 'POST',
  }),
  'orders/route-calculation/get': createEndpoint<RoutingTypes.API_OrderGetRouteCalculationResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/orders/route-calculation/:id',
    method: 'GET',
  }),
});

const invoiceEndpoints = defineEndpoints({
  'invoices/list': createEndpoint<RoutingTypes.API_InvoiceListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/invoices/list',
    method: 'GET',
  }),
  'invoices/get': createEndpoint<RoutingTypes.API_InvoiceGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/invoices/get/:id',
    method: 'GET',
  }),
  'invoices/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Invoice>({
    url: '/invoices/create',
    method: 'POST',
  }),
  'invoices/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Invoice>({
    url: '/invoices/update/:id',
    method: 'PUT',
  }),
  'invoices/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/invoices/delete/:id',
    method: 'DELETE',
  }),
});

const pricelistEndpoints = defineEndpoints({
  'pricelists/list': createEndpoint<RoutingTypes.API_PricelistListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/pricelists/list',
    method: 'GET',
  }),
  'pricelists/get': createEndpoint<RoutingTypes.API_PricelistGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/pricelists/get/:id',
    method: 'GET',
  }),
  'pricelists/create': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Pricelist>({
    url: '/pricelists/create',
    method: 'POST',
  }),
  'pricelists/update': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_Pricelist>({
    url: '/pricelists/update/:id',
    method: 'PUT',
  }),
  'pricelists/delete': createEndpoint<RoutingTypes.API_GenericCRUDResponse, RoutingTypes.API_GenericErrorResponse, RoutingTypes.API_GenericDeleteRequest>({
    url: '/pricelists/delete/:id',
    method: 'DELETE',
  }),
});

const routeEndpoints = defineEndpoints({
  'build-route/get': createEndpoint<RoutingTypes.API_MapboxDirectionsResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/build-route',
    method: 'GET',
  }),
  'build-bars/get': createEndpoint<RoutingTypes.API_BarsResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/build-bars',
    method: 'GET',
  }),
  // 'routes/mapdata': createEndpoint<RoutingTypes.API_MapDataResponse, RoutingTypes.API_GenericErrorResponse>({
  //   url: '/mapdata',
  //   method: 'GET',
  // }),
  'routes/mapdata/get': createEndpoint<RoutingTypes.API_MapDataResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/routes/mapdata/get/:id',
    method: 'GET',
  }),
});

const opportunityEndpoints = defineEndpoints({
  'opportunities/list': createEndpoint<RoutingTypes.API_OpportunityListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/opportunities/list',
    method: 'GET',
  }),
  'opportunities/get': createEndpoint<RoutingTypes.API_OpportunityGetResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/opportunities/get/:id',
    method: 'GET',
  }),
  'routes/list': createEndpoint<RoutingTypes.API_RouteListResponse, RoutingTypes.API_GenericErrorResponse>({
    url: '/routes/list',
    method: 'GET',
  }),
});

export const api_endpoints = {
  ...configEndpoints,
  ...registrationEndpoints,
  ...authEndpoints,
  ...inboxEndpoints,
  ...locationsEndpoints,
  ...vehicleEndpoints,
  ...depotEndpoints,
  ...operationsEndpoints,
  ...capabilitiesEndpoints,
  ...lineassignmentEndpoints,
  ...driverEndpoints,
  ...scheduleEndpoints,
  ...addressEndpoints,
  ...operationsEndpoints,
  ...contactEndpoints,
  ...customerEndpoints,
  ...supplierEndpoints,
  ...articleEndpoints,
  ...orderEndpoints,
  ...invoiceEndpoints,
  ...pricelistEndpoints,
  ...routeEndpoints,
  ...opportunityEndpoints,
} as const;

export type EndpointKey = keyof typeof api_endpoints;
